
import { defineComponent, ref, toRef, onMounted, onUnmounted } from 'vue'
import { Scrollbar, Autoplay, Navigation } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/vue'
import SwiperNavBtn from '@/components/common/SwiperNavBtn.vue'
import 'swiper/css'
import 'swiper/css/scrollbar'
import 'swiper/css/autoplay'
import 'swiper/css/navigation'
import isMobile from 'ismobilejs'

interface MediaItem {
  Image: {
    PcWeb: string
    MobileWeb: string
  } | string
  Alternation: string
  Rank: number
  IsCover: number
  Link?: string
}

export default defineComponent({
  name: 'g-swiper-square-image',
  components: {
    Swiper,
    SwiperSlide,
    SwiperNavBtn
  },
  props: {
    customClass: {
      type: String,
      required: false,
      default: ''
    },
    autoPlay: {
      type: [Object, Boolean],
      require: false,
      default: false
    },
    height: {
      type: [String],
      require: false,
      default: '600px'
    },
    imageType: {
      type: [String],
      require: false,
      default: 'normal'
    },
    selectItem: {
      type: Array as () => MediaItem[],
      require: false,
      default: () => [
        {
          Image: {
            PcWeb: 'https://fakeimg.pl/600x600/FFFFFF/?text=%20',
            MobileWeb: 'https://fakeimg.pl/375x375/FFFFFF/?text=%20'
          },
          Alternation: '',
          Rank: 1,
          IsCover: 1
        }
      ]
    }
  },
  setup (props) {
    const selectItemRef = toRef(props, 'selectItem')
    const imageTypeRef = toRef(props, 'imageType')
    const controlledSwiper = ref(null)
    const activeVideoElement = ref<HTMLVideoElement | null>(null)
    const isUserPaused = ref(false)

    const isVideo = (url: string) => {
      if (!url) return false
      const videoExtensions = ['.mp4', '.mov', '.MP4', '.MOV']
      return videoExtensions.some(ext => url.endsWith(ext))
    }

    const handleSlideChange = () => {
      const videos = document.querySelectorAll('.video-player')
      videos.forEach((video) => {
        if (video instanceof HTMLVideoElement) {
          video.pause()
          video.currentTime = 0
        }
      })
      isUserPaused.value = false
    }

    const handleSlideChangeEnd = (swiper: any) => {
      if (isUserPaused.value) {
        return
      }

      const _currentIndex = isMobile().phone ? selectItemRef.value.length + swiper.activeIndex : swiper.activeIndex
      const currentSlide = swiper.slides[_currentIndex]
      const isPhone = isMobile().phone
      const videoSelector = isPhone ? '.sm\\:hidden .video-player' : '.hidden.sm\\:block .video-player'
      const video = currentSlide.querySelector(videoSelector) as HTMLVideoElement

      if (video && !video.paused) {
        return
      }

      if (video) {
        console.log('找到影片元素:', video)
        pauseAllVideos()
        activeVideoElement.value = video
        video.currentTime = 0
        video.muted = false

        const playPromise = video.play()
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            console.error('自動播放失敗:', error)
            video.muted = true
            video.play().catch(e => {
              console.error('靜音播放也失敗:', e)
            })
          })
        }
      }
    }

    const pauseAllVideos = () => {
      const videos = document.querySelectorAll('.video-player')
      videos.forEach((video) => {
        if (video instanceof HTMLVideoElement && video !== activeVideoElement.value) {
          video.pause()
        }
      })
    }

    const setControlledSwiper = (swiper: any) => {
      controlledSwiper.value = swiper
    }

    const goToLink = (url?:string) => {
      if (!url) return
      document.getElementById('app')?.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      })
      window.location.href = url
    }

    const prevClick = () => {
      pauseAllVideos()
      ;(controlledSwiper.value as any).slidePrev(500, true)
    }

    const nextClick = () => {
      pauseAllVideos()
      ;(controlledSwiper.value as any).slideNext(500, true)
    }

    const togglePlay = (event: MouseEvent) => {
      event.preventDefault()
      event.stopPropagation()

      const video = event.target as HTMLVideoElement
      if (video instanceof HTMLVideoElement) {
        const rect = video.getBoundingClientRect()
        const clickY = event.clientY - rect.top
        if (clickY >= rect.height - 40) {
          return
        }

        if (video.paused) {
          pauseAllVideos()
          activeVideoElement.value = video
          isUserPaused.value = false
          const playPromise = video.play()
          if (playPromise !== undefined) {
            playPromise.catch(error => {
              console.error('播放失敗:', error)
              video.muted = true
              video.play().catch(e => {
                console.error('靜音播放也失敗:', e)
              })
            })
          }
        } else {
          video.pause()
          if (activeVideoElement.value === video) {
            activeVideoElement.value = null
          }
          isUserPaused.value = true
        }
      }
    }

    const handleResize = () => {
      if (activeVideoElement.value) {
        activeVideoElement.value.pause()
        activeVideoElement.value = null
        isUserPaused.value = false
      }
    }

    onMounted(() => {
      window.addEventListener('resize', handleResize)
    })

    onUnmounted(() => {
      window.removeEventListener('resize', handleResize)
    })

    return {
      modules: [Scrollbar, Autoplay, Navigation],
      controlledSwiper,
      setControlledSwiper,
      prevClick,
      nextClick,
      selectItemRef,
      imageTypeRef,
      isVideo,
      handleSlideChange,
      handleSlideChangeEnd,
      isMobile,
      goToLink,
      togglePlay
    }
  }
})
